import { Callout } from "../components/Callout.js";
import { ScreenshotBox } from "../components/ScreenshotBox.js";

export const meta = {
  title: "テンプレートを使った送信",
  updatedAt: "2026-07-13",
};

export function Content() {
  return (
    <>
      <p>
        Sign Extension は、HubSpot からファイルをアップロードして送信する方式に加えて、
        <strong>クラウドサインに登録済みのテンプレートを呼び出して送信</strong>する方式にも
        対応しています。同じ書式で繰り返し送る書類（NDA、業務委託契約書、注文書 など）に
        向いています。
      </p>

      <h2>2 つの送信方式の使い分け</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", margin: "12px 0", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#faf9f7" }}>
            <th style={th()}></th>
            <th style={th()}>ファイルアップロード方式</th>
            <th style={th()}>テンプレート方式</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={td()}>向いているケース</td>
            <td style={td()}>案件ごとに PDF が異なる（オーダーメイドの契約書）</td>
            <td style={td()}>同じ書式を繰り返し使う（NDA・注文書・入社書類 等）</td>
          </tr>
          <tr>
            <td style={td()}>ファイル準備</td>
            <td style={td()}>毎回 PDF を用意</td>
            <td style={td()}>クラウドサイン側に事前登録のみで OK</td>
          </tr>
          <tr>
            <td style={td()}>捺印位置</td>
            <td style={td()}>クラウドサイン画面で毎回配置</td>
            <td style={td()}>テンプレートに事前登録済み</td>
          </tr>
          <tr>
            <td style={td()}>署名者（社内承認者）</td>
            <td style={td()}>固定送付宛先で毎回自動追加</td>
            <td style={td()}>テンプレートに事前定義 + 固定送付宛先も併用可</td>
          </tr>
        </tbody>
      </table>

      <h2>事前準備：クラウドサインでテンプレートを作る</h2>
      <p>
        まずクラウドサインの管理画面でテンプレートを作成します（Sign Extension 側の操作ではありません）。
      </p>
      <ol>
        <li>クラウドサイン管理画面にログイン</li>
        <li>「書類の管理」→「テンプレート」から新規テンプレートを作成</li>
        <li>PDF をアップロードし、捺印位置・入力欄などのウィジェットを配置</li>
        <li>必要に応じて署名者（社内承認者）を事前定義</li>
        <li>保存 → テンプレート一覧に表示される状態にする</li>
      </ol>

      <Callout type="info" title="共有テンプレートも対象">
        クラウドサインのチーム内で共有されているテンプレートも、Sign Extension から利用できます。
      </Callout>

      <h2>手順 1：Sign Extension で既定テンプレートを設定（任意）</h2>
      <p>
        頻繁に使うテンプレートは、オブジェクト（取引・会社・コンタクト）ごとに既定として
        設定できます。既定にしておくと、送信モーダルを開いた時に自動選択されます。
      </p>
      <ol>
        <li>ダッシュボードの「<strong>プロパティ</strong>」タブを開く</li>
        <li>対象オブジェクトの「設定する」をクリック</li>
        <li>下の方にある「<strong>クラウドサインテンプレート</strong>」セクションで
          「<strong>テンプレート一覧を取得</strong>」をクリック</li>
        <li>ドロップダウンから既定にするテンプレートを選択</li>
        <li>「マッピングを保存」で確定</li>
      </ol>
      <ScreenshotBox src="/guide/screenshots/templates-1.png" caption="デフォルト設定でのテンプレート指定" />

      <p>
        既定を設定しない場合は、送信モーダルで毎回テンプレートを選ぶ運用になります。
      </p>

      <h2>手順 2：送信モーダルでテンプレートを使う</h2>
      <ol>
        <li>HubSpot レコードから「クラウドサインで送信」で送信モーダルを開く</li>
        <li>STEP 1（ファイル選択・順序）の上部にある
          「<strong>クラウドサインのテンプレートを利用する</strong>」にチェックを入れる</li>
        <li>ドロップダウンからテンプレートを選択（既定設定済みなら自動選択されています）</li>
        <li>そのまま STEP 2（宛先指定）→ STEP 3（確認・送信）へ進む</li>
      </ol>
      <ScreenshotBox src="/guide/screenshots/templates-2.png" caption="送信モーダルのテンプレート選択" />

      <Callout type="tip" title="ファイルアップロードはスキップされる">
        テンプレート利用時、STEP 1 のファイル選択セクションは非表示になります。
        テンプレートに紐付く PDF・ウィジェット・参加者が新書類に自動で複製されます。
      </Callout>

      <h2>宛先・共有先の取り扱い</h2>
      <p>
        テンプレートに事前定義された署名者は<strong>そのまま維持</strong>され、これに加えて
        HubSpot 側の宛先が追加登録されます。
      </p>
      <ul>
        <li>
          <strong>テンプレートの署名者</strong>（例: 社内承認者・代表印）→ 元の順序で保持
        </li>
        <li>
          <strong>Sign Extension 側で追加</strong>される宛先:
          <ul>
            <li>HubSpot プロパティから取得した送付先</li>
            <li>「デフォルト設定」→「送付宛先」の「固定送付宛先」に登録した社内承認者</li>
            <li>関連コンタクトや手動入力で追加した宛先</li>
          </ul>
        </li>
        <li>
          <strong>共有先（CC）</strong>: プロパティ or モーダル入力から追加
        </li>
      </ul>

      <Callout type="warning" title="署名順に注意">
        テンプレートの署名者と HubSpot 側で追加した宛先が重複する可能性があるため、
        送信モーダルの STEP 2 で必ず一覧を確認し、必要に応じて順序調整や削除を行ってください。
      </Callout>

      <h2>送信後の動作</h2>
      <p>
        送信後は通常のファイル送信と同じ挙動になります。
      </p>
      <ul>
        <li>HubSpot レコードのプロパティに書類 ID・下書き URL・ステータスが書き込み</li>
        <li>15 分間隔でクラウドサインのステータスを自動同期</li>
        <li>締結後は署名済 PDF・合意締結証明書が HubSpot に自動アップロード（設定次第）</li>
      </ul>

      <h2>制限事項</h2>
      <ul>
        <li>
          <strong>ウィジェット値の自動入力は未対応</strong>: テンプレートのテキストボックスや
          チェックボックスに、HubSpot プロパティの値を自動で流し込む機能はまだ実装されていません。
          必要な値はクラウドサイン画面から手動入力してください
        </li>
        <li>
          <strong>ファイルアップロードとの併用不可</strong>: 1 回の送信でテンプレートと
          追加 PDF を組み合わせることはできません
        </li>
      </ul>

      <Callout type="info" title="関連ガイド">
        ・<a href="/guide/send">書類を送信する</a>（送信モーダル全体の流れ）<br />
        ・<a href="/guide/property-setup">HubSpot プロパティ設定</a>（既定テンプレート含む）<br />
        ・<a href="/guide/recipients">固定送付宛先・共有先（CC）</a>（署名者の重複を避けるコツ）
      </Callout>
    </>
  );
}

function th() {
  return { textAlign: "left" as const, padding: "8px 12px", borderBottom: "1px solid #d0cfc9" };
}
function td() {
  return { padding: "8px 12px", borderBottom: "1px solid #f0efeb" };
}
